/* eslint-disable react/no-multi-comp */
import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Heading, Paragraph } from '@scentregroup/ui'
import classNames from 'classnames'
import {
  storeIsOpen,
  storeIsOpeningSoon,
  isAfterDate,
} from '../../helpers/store-helper'
import { useEvolvedContext } from '../../context'
import UtilityBox from '../utility-box'
import ExternalLink from '../external-link'
import UtilityBoxItem from '../utility-box-item'
import Image from '../image'
import { parseStoreHours } from '../../helpers/parse-opening-hours/parse-store-hours'
import UtilityBoxOpeningHours from '../utility-box-opening-hours'
import style from './index.module.scss'

const StoreStatus = props => {
  const { status } = props
  if (status && !storeIsOpen(status)) {
    return (
      <UtilityBoxItem icon="hours">
        <Paragraph type="medium">{status}</Paragraph>
      </UtilityBoxItem>
    )
  }
  return null
}

function hasHours(openHours) {
  return (
    openHours.storeTradingSchedule &&
    openHours.centreTradingSchedule &&
    openHours.timeZone
  )
}

const StoreOpeningHours = props => {
  const { status, openHours } = props
  // If a store has a status, it must be open
  if (hasHours(openHours) && status && storeIsOpen(status)) {
    const parsedHours = parseStoreHours(
      openHours.storeTradingSchedule,
      openHours.centreTradingSchedule,
      openHours.timeZone
    )
    return <UtilityBoxOpeningHours openingHours={parsedHours} />
  }
  return null
}

const StoreLocation = props => {
  const { status, location, nearestLocation, locationLink, openAt } = props
  const displayLink =
    status &&
    (storeIsOpen(status) ||
      (storeIsOpeningSoon(status) && isAfterDate(openAt))) &&
    (location || nearestLocation || locationLink)

  if (displayLink) {
    return (
      <UtilityBoxItem
        icon="location-drop-pin"
        role="heading"
        aria-label="Store Location"
        aria-level="4"
      >
        {location && (
          <Paragraph type="medium" className={style.location}>
            {location}
          </Paragraph>
        )}
        {nearestLocation && (
          <Paragraph type="medium">{nearestLocation}</Paragraph>
        )}
        {locationLink && (
          <Link className={style.mapLink} to={locationLink}>
            See on map
          </Link>
        )}
      </UtilityBoxItem>
    )
  }
  return null
}

const RetailerUtilityBox = ({
  className,
  heading,
  status,
  openHours,
  phoneNumber,
  emailAddress,
  location,
  link,
  linkText,
  locationLink,
  nearestLocation,
  parkingLocation,
  storeImage,
  storeLink,
  openAt,
}) => {
  const isEvolved = useEvolvedContext()

  return (
    <UtilityBox
      className={classNames(style.UtilityBox, className, {
        [style.evolved]: isEvolved,
      })}
    >
      <div className={style.utilityInfoWrapper}>
        <Heading type="small" className={style.heading}>
          {storeLink ? <Link to={storeLink}> {heading} </Link> : heading}
        </Heading>
        <StoreStatus status={status} />
        {openHours && (
          <StoreOpeningHours status={status} openHours={openHours} />
        )}

        {phoneNumber && (
          <UtilityBoxItem
            icon="phone"
            role="heading"
            aria-label="Phone Number"
            aria-level="4"
          >
            <Paragraph type="medium">
              <ExternalLink url={`tel:${phoneNumber}`} rel="">
                {phoneNumber}
              </ExternalLink>
            </Paragraph>
          </UtilityBoxItem>
        )}

        {emailAddress && (
          <UtilityBoxItem
            icon="email"
            role="heading"
            aria-label="Email Address"
            aria-level="4"
          >
            <Paragraph type="medium">
              <ExternalLink url={`mailto:${emailAddress}`}>
                Email us
              </ExternalLink>
            </Paragraph>
          </UtilityBoxItem>
        )}

        {Boolean(link) && (
          <UtilityBoxItem
            icon="website"
            role="heading"
            aria-label="Website"
            aria-level="4"
          >
            <Paragraph type="medium">
              <ExternalLink url={link}>{linkText}</ExternalLink>
            </Paragraph>
          </UtilityBoxItem>
        )}

        <StoreLocation
          status={status}
          location={location}
          nearestLocation={nearestLocation}
          locationLink={locationLink}
          openAt={openAt}
        />

        {parkingLocation && (
          <UtilityBoxItem
            icon="parking"
            role="heading"
            aria-label="Parking"
            aria-level="4"
          >
            <Paragraph type="medium">{parkingLocation}</Paragraph>
          </UtilityBoxItem>
        )}
      </div>
      {Boolean(storeImage) && (
        <Image
          className={style.storeImage}
          imageUrl={storeImage}
          ratio="standard"
          alt="Storefront"
        />
      )}
    </UtilityBox>
  )
}

const rawStoreHours = PropTypes.shape({
  storeTradingSchedule: PropTypes.shape({
    tradingHours: PropTypes.array.isRequired,
    isStoreCentreLinked: PropTypes.bool.isRequired,
  }),
  centreTradingSchedule: PropTypes.shape({
    tradingHours: PropTypes.array.isRequired,
  }),
  timeZone: PropTypes.string.isRequired,
}).isRequired

RetailerUtilityBox.propTypes = {
  className: PropTypes.string,
  openAt: PropTypes.string,
  storeLink: PropTypes.string,
  heading: PropTypes.string,
  status: PropTypes.string,
  openHours: rawStoreHours,
  phoneNumber: PropTypes.string,
  location: PropTypes.string,
  emailAddress: PropTypes.string,
  link: PropTypes.string,
  linkText: PropTypes.string,
  locationLink: PropTypes.string,
  nearestLocation: PropTypes.string,
  parkingLocation: PropTypes.string,
  storeImage: PropTypes.string,
}

StoreStatus.propTypes = {
  status: PropTypes.string,
}

StoreOpeningHours.propTypes = {
  status: PropTypes.string,
  openHours: rawStoreHours,
}

StoreLocation.propTypes = {
  status: PropTypes.string,
  location: PropTypes.string,
  nearestLocation: PropTypes.string,
  locationLink: PropTypes.string,
  openAt: PropTypes.string,
}

export default RetailerUtilityBox
